// Per-term scoring breakdown for the NFL matchup view.
//
// WHY A CLIENT MIRROR EXISTS AT ALL. The authoritative scorer is
// supabase/functions/_shared/nfl-sabotage-scoring.ts, a Deno module the Vite client cannot
// import. The engine persists only TOTALS (trash_points / anti_points), not per-term detail,
// so showing a user why a player scored what they scored means recomputing the terms here.
//
// THAT IS A SECOND IMPLEMENTATION, AND SECOND IMPLEMENTATIONS DRIFT. Two things contain it:
//
//   1. nflScoringBreakdown.test.ts reads the edge-function source and fails if the arithmetic
//      here stops matching it. Same discipline as nflSlots.test.ts mirroring nfl-lineup.
//   2. Every breakdown is RECONCILED against the stored total before it is shown. If the
//      terms do not sum to what the engine wrote, the caller must render the stored score and
//      say the detail is unavailable -- never a breakdown that does not add up. On a feature
//      whose whole purpose is making an unintuitive number trustworthy, a plausible-but-wrong
//      breakdown is worse than none.
//
// MLB's equivalent (MatchupBoxScore.tsx) multiplies raw x weight. That is correct for MLB,
// whose model is a flat Record<stat, number>. It is WRONG for 11 of the NFL model's 86 terms:
// per_10_yards, per_100_yards, per_point and per_7_band all have denominators, and
// tiered_rate has no scalar at all. applyUnit below is a verbatim mirror, not an
// approximation.

export interface ModelTerm {
  applies_to: 'player' | 'dst' | 'coach';
  category: string;
  event: string;
  points: number;
  unit: string;
  source: string;
  /**
   * SAB-1127: this term can RETREAT while a game is in progress (team-outcome
   * weights). Withheld until the game is final so a live score cannot walk
   * backwards. Mirrors the same flag in _shared/nfl-sabotage-scoring.ts -- the two
   * must agree or the reconciliation below blocks the panel.
   */
  reversible?: boolean;
  tier_config?: {
    position: string;
    qualifier: { source: string; min: number };
    numerator: string;
    denominator: string;
    tiers: { min?: number; points: number }[];
  };
}

export interface Contribution {
  event: string;
  source: string;
  unit: string;
  points: number;   // the model/override weight
  raw: number;      // the stat value it was applied to
  pts: number;      // the resulting contribution
}

export interface Breakdown {
  contributions: Contribution[];
  trash: number;
  anti: number;
}

/** Why a breakdown could not be shown. `null` means it reconciled and is safe to render. */
export type BreakdownBlockedReason = 'no-stats' | 'missing-context' | 'mismatch';

// ─── verbatim mirrors of the edge function ──────────────────────────────────
// Any edit here must be made in supabase/functions/_shared/nfl-sabotage-scoring.ts first.
// nflScoringBreakdown.test.ts reads that file and fails if these diverge.

export function readSource(stats: Record<string, unknown>, source: string): number {
  const dot = source.indexOf('.');
  if (dot < 0) return 0;
  const category = source.slice(0, dot);
  const field = source.slice(dot + 1);
  const cat = (stats as Record<string, Record<string, unknown>>)?.[category];
  if (!cat || typeof cat !== 'object') return 0;
  const v = Number(cat[field]);
  return Number.isFinite(v) ? v : 0;
}

export function applyUnit(points: number, unit: string, value: number): number {
  switch (unit) {
    case 'flat': return points * value;
    case 'per_100_yards': return points * (value / 100);
    case 'per_10_yards': return points * (value / 10);
    case 'per_point': return points * value;
    case 'per_full_10': return points * Math.floor(value / 10);
    case 'per_7_band': return value < 1 ? 0 : points * Math.min(3, Math.floor((value - 1) / 7));
    default: throw new Error(`[sabotage-scoring] unknown unit '${unit}'`);
  }
}

const round2 = (n: number) => Math.round(n * 100) / 100;

// ─── per-role breakdowns ────────────────────────────────────────────────────

/**
 * A PLAYER breakdown needs scoring context the stats blob does not carry: `team_lost` drives
 * the team-loss bonus, and `position` gates every tiered_rate ladder. Both live in columns on
 * nfl_player_game_stats that were only added by SAB-825, so rows ingested before it have NULL
 * for both and CANNOT be reproduced. Callers must check this first -- guessing `false`/`null`
 * silently drops real points and the reconciliation would fail for a reason the user cannot
 * see.
 */
export function hasPlayerContext(row: { team_lost: boolean | null; position: string | null }): boolean {
  return row.team_lost !== null && row.position !== null;
}

function breakdownPlayer(
  stats: Record<string, unknown>,
  terms: ModelTerm[],
  ctx: { teamLost: boolean; position: string | null },
): Breakdown {
  const contributions: Contribution[] = [];
  let trash = 0;
  let anti = 0;

  for (const w of terms) {
    if (w.applies_to !== 'player') continue;

    if (w.unit === 'tiered_rate') {
      // SAB-1165: zero means off. MUST match the guard in
      // _shared/nfl-sabotage-scoring.ts -- a client that shows a contribution the
      // server does not score is SAB-1159's divergence in another form.
      if (w.points === 0) continue;
      const cfg = w.tier_config;
      if (!cfg || (ctx.position ?? '') !== cfg.position) continue;
      if (readSource(stats, cfg.qualifier.source) < cfg.qualifier.min) continue;
      const den = readSource(stats, cfg.denominator);
      if (den <= 0) continue;
      const rate = readSource(stats, cfg.numerator) / den;
      let pts = 0;
      for (const t of cfg.tiers) {
        if (t.min === undefined || rate >= t.min) { pts = t.points; break; }
      }
      if (pts >= 0) trash += pts; else anti += pts;
      if (pts !== 0) {
        contributions.push({ event: w.event, source: w.source, unit: w.unit, points: pts, raw: round2(rate), pts });
      }
      continue;
    }

    let value: number;
    if (w.source === 'derived:game_result') value = ctx.teamLost ? 1 : 0;
    else if (w.source.startsWith('derived:')) continue;
    else value = readSource(stats, w.source);

    const contrib = applyUnit(w.points, w.unit, value);
    if (contrib >= 0) trash += contrib; else anti += contrib;
    if (contrib !== 0) {
      contributions.push({ event: w.event, source: w.source, unit: w.unit, points: w.points, raw: value, pts: contrib });
    }
  }

  return { contributions, trash: round2(trash), anti: round2(anti) };
}

/**
 * DST and COACH read their context out of the stored stats blob (`stats.dst` / `stats.coach`),
 * which the ingest has always written. They therefore reconcile on EVERY row, including ones
 * predating SAB-825 -- only the player path is gated.
 */
function resolveDstValue(dst: Record<string, unknown>, source: string): number {
  const num = (v: unknown) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
  if (source === 'derived:game_result') return dst.team_lost === true ? 1 : 0;
  if (source === 'derived:opponent_score') return num(dst.points_allowed);
  if (source === 'derived:opponent_score==0') return dst.shutout === true ? 1 : 0;
  if (source === 'derived:opponent_pass+rush_yards') return num(dst.yards_allowed);
  if (source === 'derived:team_penalties.penalties') return num(dst.def_penalties);
  if (source === 'derived:team_penalties.yards') return num(dst.def_penalty_yards);
  if (source === 'derived:team_penalties.first_downs') return num(dst.def_penalty_first_downs);
  // The compound return-TD source: the ingest flattens all three into `return_tds`.
  if (source.includes('+')) return num(dst.return_tds);
  const dot = source.indexOf('.');
  return dot < 0 ? 0 : num(dst[source.slice(dot + 1)]);
}

function resolveCoachValue(coach: Record<string, unknown>, term: ModelTerm): number {
  const num = (v: unknown) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
  if (term.source === 'derived:game_result') {
    // Resolved BY EVENT: three terms share this source (Team loss / win / tie).
    if (term.event === 'Team loss') return coach.team_lost === true ? 1 : 0;
    if (term.event === 'Team win') return coach.team_won === true ? 1 : 0;
    if (term.event === 'Team tie') return coach.team_tied === true ? 1 : 0;
    return 0;
  }
  if (term.source === 'derived:own_score==0') return num(coach.own_score) === 0 ? 1 : 0;
  if (term.source === 'derived:loss_margin') return num(coach.loss_margin);
  if (term.source === 'derived:team_penalties.penalties') return num(coach.team_penalties);
  if (term.source === 'derived:team_penalties.first_downs') return num(coach.team_penalty_first_downs);
  return 0;
}

function breakdownRole(
  blob: Record<string, unknown>,
  terms: ModelTerm[],
  role: 'dst' | 'coach',
): Breakdown {
  const contributions: Contribution[] = [];
  let trash = 0;
  let anti = 0;
  for (const w of terms) {
    if (w.applies_to !== role) continue;
    const value = role === 'dst' ? resolveDstValue(blob, w.source) : resolveCoachValue(blob, w);
    const contrib = applyUnit(w.points, w.unit, value);
    if (contrib >= 0) trash += contrib; else anti += contrib;
    if (contrib !== 0) {
      contributions.push({ event: w.event, source: w.source, unit: w.unit, points: w.points, raw: value, pts: contrib });
    }
  }
  return { contributions, trash: round2(trash), anti: round2(anti) };
}


// ─── per-league override merge ──────────────────────────────────────────────

/**
 * Mirror of resolveLeagueModel's MERGE RULE: only `points` is taken from an override, keyed
 * on (applies_to, source, event). unit / source / tier_config always come from the trusted
 * model, so an override can reweight a rule but never redefine one.
 *
 * The 3-key matters here as much as on the server: coach `derived:game_result` covers Team
 * loss / win / tie and player `derived:tiered_rate` covers three YPR terms, so keying on
 * (applies_to, source) would apply one value across every member of a collision group.
 *
 * Omitting this would fail SAFE rather than wrong -- an override league's breakdown simply
 * would not reconcile, and the caller would show nothing. It is implemented so those leagues
 * get a breakdown at all.
 */
export function resolveTerms(terms: ModelTerm[], overrides: unknown): ModelTerm[] {
  if (!Array.isArray(overrides) || overrides.length === 0) return terms;
  const SEP = '\0';
  const key = (a: string, s: string, e: string) => a + SEP + s + SEP + e;
  const byKey = new Map<string, number>();
  for (const raw of overrides) {
    const o = raw as Record<string, unknown> | null;
    if (!o || typeof o !== 'object') continue;
    if (typeof o.applies_to !== 'string' || typeof o.source !== 'string' || typeof o.event !== 'string') continue;
    if (typeof o.points !== 'number' || !Number.isFinite(o.points)) continue;
    byKey.set(key(o.applies_to, o.source, o.event), o.points);
  }
  if (byKey.size === 0) return terms;
  return terms.map(t => {
    const k = key(t.applies_to, t.source, t.event);
    return byKey.has(k) ? { ...t, points: byKey.get(k) as number } : t;
  });
}

// ─── public entry point ─────────────────────────────────────────────────────

export interface BreakdownInput {
  stats: Record<string, unknown> | null;
  team_lost: boolean | null;
  position: string | null;
  /**
   * SAB-1127: whether the source game was final when the engine scored this row.
   * NULL means the row predates the column and is settled history from a final
   * game, so it is treated as final -- matching the migration's backfill and the
   * matchup engine's re-score default.
   */
  outcome_final: boolean | null;
  /** What the engine actually wrote. The breakdown must reproduce these or it is not shown. */
  storedTrash: number;
  storedAnti: number;
}

export interface BreakdownResult {
  breakdown: Breakdown | null;
  blocked: BreakdownBlockedReason | null;
}

/**
 * Compute a per-term breakdown and RECONCILE it against the stored total.
 *
 * Returns `blocked` instead of a breakdown whenever the detail cannot be trusted:
 *   'no-stats'         no stats row for this player-game
 *   'missing-context'  a player row predating SAB-825's context columns
 *   'mismatch'         the terms did not sum to what the engine wrote
 *
 * A caller must render the stored score and an explanation for every blocked reason. It must
 * never fall back to showing the contributions anyway.
 */
export function computeBreakdown(input: BreakdownInput, allTerms: ModelTerm[]): BreakdownResult {
  const { stats } = input;
  if (!stats || typeof stats !== 'object') return { breakdown: null, blocked: 'no-stats' };

  // SAB-1127: drop the reversible team-outcome terms while the game is live, exactly
  // as the engine did when it wrote trash_points/anti_points. If these two ever
  // disagree the reconciliation below catches it and blocks the panel rather than
  // showing badges that do not sum to the header -- but agreeing is the point.
  const outcomeFinal = input.outcome_final !== false;
  const terms = outcomeFinal
    ? allTerms
    : allTerms.filter(t => t.reversible !== true);

  let b: Breakdown;
  if (stats.dst && typeof stats.dst === 'object') {
    b = breakdownRole(stats.dst as Record<string, unknown>, terms, 'dst');
  } else if (stats.coach && typeof stats.coach === 'object') {
    b = breakdownRole(stats.coach as Record<string, unknown>, terms, 'coach');
  } else {
    if (!hasPlayerContext(input)) return { breakdown: null, blocked: 'missing-context' };
    b = breakdownPlayer(stats, terms, {
      teamLost: input.team_lost === true,
      position: input.position,
    });
  }

  // Reconciliation. Compare on the same 2-decimal rounding the engine applies, so float noise
  // cannot fail an otherwise-correct breakdown.
  if (round2(b.trash) !== round2(input.storedTrash) || round2(b.anti) !== round2(input.storedAnti)) {
    return { breakdown: null, blocked: 'mismatch' };
  }
  return { breakdown: b, blocked: null };
}
